import { ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ApplicationFormService } from '../application-form/application-form.service';
import { ApplicantService } from './applicant.service';
import { DataService } from '@app/core/services/data.service';
import { ApplicationFormDataService } from '../application-form/application-form-data.service';
import { BehaviorSubject, Observable, Subject, firstValueFrom, of, takeUntil } from 'rxjs';
import { SlideCommonService } from '../common/slide-common.service';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { WINDOW, WINDOW_PROVIDERS } from '@app/shared/services/window/window.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { formatFormErrors } from '@app/shared/formly/form-config';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AsyncPipe, CommonModule, NgFor, NgIf } from '@angular/common';
import { ConnectSlideComponent } from '@app/blocks/ui/slide/slide.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';

@Component({
    selector: 'app-applicant',
    templateUrl: './applicant.component.html',
    providers: [
        ApplicantService,
        WINDOW_PROVIDERS
    ],
    imports: [
        CommonModule,
        ConnectSlideComponent,
        SpinnerComponent,
        FormlyModule,
        AsyncPipe,
        NgIf,
        NgFor
    ]
})
export class ApplicantComponent implements OnInit, OnDestroy {
    @Input() slideNumber: string | undefined;
    @Input() finalSlide = false;
    currentSlide = 'applicant';
    active = this.afs.getActive();
    showError = false; //applicant loading error
    showFormErrors = false; //error summary
    errors: any = [];
    jointApplication = this.afs.getJointApplication();
    showSubmit = false;
    isDisabled = false;
    loading = true;
    submitting = false;
    applicationId = sessionStorage.getItem('connect.applicationId');
    isValid = false;
    schema = null;
    formState = this.afs.getFormState();
    leadText = "";

    form: FormGroup = new FormGroup(
        {}
    );
    fields: BehaviorSubject<any> = new BehaviorSubject([]);

    model: any = this.afs.getLeadModel();

    leadModelSubscription = this.afs.subscribeLeadModel((data: any) => {
        console.log('setting applicant model via leadModelSubscription')
        this.model = data;
    });

    referenceData = this.afs.getRefDataModel();

    refDataSubscription = this.afs.subscribeRefDataModel((data: any) => {
        console.log('setting referenceData via subscription');
        this.referenceData = data;
    })

    changeFns = {
        jointApplication: (val: boolean) => this.onJointApplication(val),
    }

    globalValidators = {
        lengthOfTimeAtProperty: this.lengthOfTimeAtProperty
    }


    private destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private applicationFormDataService: ApplicationFormDataService,
        private afs: ApplicationFormService,
        private applicantService: ApplicantService,
        private slideCommon: SlideCommonService,
        private el: ElementRef,
        private logger: LoggerService,
        @Inject(WINDOW) private window: Window,
        private cdr: ChangeDetectorRef
    ) {
    }

    onSubmit() {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.errors = [];
            this.showFormErrors = false;

            this.isDisabled = true;
            this.submitting = true;

            this.afs.addAddressIds("lead");

            firstValueFrom(this.applicationFormDataService.postApplicant(this.model)).then((response: any) => {
                console.log('applicant post success ' + response.status);

                this.slideCommon.save(this.form, this.currentSlide, this.formState, this.active);
                this.submitting = false;
                this.isDisabled = false;

                this.cdr.markForCheck();
            }).catch((error) => {
                this.submitting = false;
                this.logger.error(error);
            });
        }
        else {
            const renderTime = 500;
            const revealTime = renderTime + 250;

            setTimeout(() => {
                this.errors = formatFormErrors(this.el, this.form);
                this.showFormErrors = true;
            }, renderTime);

            setTimeout(() => {
                this.scrollToId('error-summary');
            }, revealTime);
        }
    }

    ngOnInit(): void {
        // prevents users accessing the application page without going via homepage
        if (this.applicationId === '' || this.applicationId === null) {
            this.logger.info('no applicationId detected');

            const redirect = this.window.location.protocol + '//' + this.window.location.host + '/';

            this.window.location = redirect;
        }

        const observables: Observable<any>[] = [
            this.dataService.getReferenceData(),
            this.applicationFormDataService.getApplicant(),
            of(this.afs.getFormState())
        ]

        this.dataService.ready(observables).pipe(takeUntil(this.destroy$)).subscribe(
            {
                next: ([ referenceData, applicant, formState ]) => {
                    this.afs.setRefDataModel(referenceData);
                    this.afs.setLeadModel(applicant);
                    this.fields.next(this.applicantService.fetchSchema(referenceData, this.changeFns, this.globalValidators));
                    this.formState = formState;

                    const app1 = applicant;

                    // //From crm it's possible to set a nationality which isnt in the shortlist - if this has happened then add the applicants nationality
                    this.dataService.ensureNationalityIsInShortList(referenceData, app1.nationality);

                    this.logger.info('getApplicant result', applicant);

                    this.loading = false; // used for spinner container

                    this.showSubmit = true;

                    if (this.model.isJointApplication === true) {
                        this.onJointApplication(true);
                    }

                    this.applicantService.setCachedNiNumber(this.model.niNumber);

                    // set the default value before on load
                    if (this.model.previousAddresses && this.model.previousAddresses.length === 0) {
                        this.model.previousAddresses = [ null ]
                    }
                    if (this.model.criminalConvictions && this.model.criminalConvictions.length === 0) {
                        this.model.criminalConvictions = [ null ]
                    }

                },
                error: () => {
                    console.log('"ready" function failed');
                }
            }
        );
    }

    scrollToId(id: string) {
        this.slideCommon.scrollToId(id, this.el, this.window);
    }

    onJointApplication(value: boolean) {
        if (value === true) {
            this.jointApplication.enabled = true;
            this.leadText = "Lead ";
        } else {
            this.jointApplication.enabled = false;
            this.leadText = "";
        }

        this.afs.setJointApplication(this.jointApplication);

        this.cdr.markForCheck();
    }

    lengthOfTimeAtProperty(c: AbstractControl, f: FormlyFieldConfig) {
        // because this code now runs hot as part of a validator (to keep sync with the other form errors), logs are disabled
        // console.log('lengthOfTimeAtPropertyChange');

        let lengthOfTimeAtCurrentProperty = 0;
        let lengthOfTimeAtPreviousProperties = 0;

        if (parseInt(f.model.durationAtCurrentProperty)) {
            lengthOfTimeAtCurrentProperty = parseInt(f.model.durationAtCurrentProperty);
        }

        if (f.model.previousAddresses) {
            for (let j = 0; j < f.model.previousAddresses.length; j++) {
                if (f.model.previousAddresses[ j ] && parseInt(f.model.previousAddresses[ j ].durationAtPreviousProperty)) {
                    lengthOfTimeAtPreviousProperties = lengthOfTimeAtPreviousProperties + parseInt(f.model.previousAddresses[ j ].durationAtPreviousProperty);
                }
            }
        }

        const totalAddressDuration = lengthOfTimeAtCurrentProperty + lengthOfTimeAtPreviousProperties;

        // console.log('totalAddressDuration ' + totalAddressDuration);

        if (totalAddressDuration < 60 && f.model.previousAddresses) {
            // console.log('triggered < 60');
            return false;

        } else if (totalAddressDuration >= 60 && f.model.previousAddresses) {
            // console.log('triggered >= 60');
            return true;
        }
        else if (totalAddressDuration >= 60 && !f.model.previousAddresses) {
            // console.log('triggered >= 60');
            return true;
        }
        return false;
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        this.leadModelSubscription.unsubscribe();
        this.refDataSubscription.unsubscribe();
    }
}
